import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as ToastPrimitive from '@radix-ui/react-toast';
import { CheckCircle2, CircleX, Info, TriangleAlert, X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useRef, useState, } from 'react';
import { cn } from '../lib/cn.js';
export const TOAST_TONE = {
    SUCCESS: 'SUCCESS',
    ERROR: 'ERROR',
    INFO: 'INFO',
    WARNING: 'WARNING',
};
export const TOAST_POSITION = {
    TOP_LEFT: 'top-left',
    TOP_CENTER: 'top-center',
    TOP_RIGHT: 'top-right',
    BOTTOM_LEFT: 'bottom-left',
    BOTTOM_CENTER: 'bottom-center',
    BOTTOM_RIGHT: 'bottom-right',
};
const ToastContext = createContext(null);
const DEFAULT_DURATION_MS = 2000;
/** Radix treats Infinity as "never auto-dismiss". */
const NEVER = Number.POSITIVE_INFINITY;
const TOAST_POSITIONS = Object.values(TOAST_POSITION);
const VIEWPORT_POSITION_CLASS = {
    [TOAST_POSITION.TOP_LEFT]: 'top-0 left-0',
    [TOAST_POSITION.TOP_CENTER]: 'top-0 left-1/2 -translate-x-1/2',
    [TOAST_POSITION.TOP_RIGHT]: 'top-0 right-0 items-end',
    [TOAST_POSITION.BOTTOM_LEFT]: 'bottom-0 left-0',
    [TOAST_POSITION.BOTTOM_CENTER]: 'bottom-0 left-1/2 -translate-x-1/2',
    [TOAST_POSITION.BOTTOM_RIGHT]: 'bottom-0 right-0 items-end',
};
const SWIPE_DIRECTION = {
    [TOAST_POSITION.TOP_LEFT]: 'left',
    [TOAST_POSITION.TOP_CENTER]: 'up',
    [TOAST_POSITION.TOP_RIGHT]: 'right',
    [TOAST_POSITION.BOTTOM_LEFT]: 'left',
    [TOAST_POSITION.BOTTOM_CENTER]: 'down',
    [TOAST_POSITION.BOTTOM_RIGHT]: 'right',
};
/**
 * Toast notifications.
 *
 * Two rules that are not Radix defaults.
 *
 * **An error toast does not disappear on its own.** A success message the user
 * misses costs nothing; a failure they miss means they believe a change was
 * saved when it was not. Errors stay until dismissed.
 *
 * **A toast is never the only report of a failure.** It is an announcement,
 * not a record: it is gone in two seconds, it cannot be re-read, and it is
 * the wrong place for anything a user needs while they act. Form errors belong
 * next to the form. This is why `FormDialog` renders its own error rather than
 * calling `toast`.
 */
export function ToastProvider({ position = TOAST_POSITION.TOP_RIGHT, children, }) {
    const [toasts, setToasts] = useState([]);
    const nextId = useRef(0);
    const dismiss = useCallback((id) => {
        setToasts((current) => current.filter((entry) => entry.id !== id));
    }, []);
    const toast = useCallback((options) => {
        nextId.current += 1;
        const id = `toast-${String(nextId.current)}`;
        setToasts((current) => [...current, { ...options, id }]);
    }, []);
    const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);
    /*
     * Radix portals each toast root into the nearest Provider's viewport, and a
     * Provider tracks exactly one viewport — so six screen corners need six
     * providers, mounted lazily for the positions that actually have toasts.
     */
    return (_jsxs(ToastContext.Provider, { value: value, children: [children, TOAST_POSITIONS.map((corner) => {
                const group = toasts.filter((entry) => (entry.position ?? position) === corner);
                if (group.length === 0)
                    return null;
                return (_jsxs(ToastPrimitive.Provider, { swipeDirection: SWIPE_DIRECTION[corner], children: [group.map((entry) => (_jsx(ToastItem, { record: entry, onDismiss: () => {
                                dismiss(entry.id);
                                entry.onDismiss?.();
                            } }, entry.id))), _jsx(ToastPrimitive.Viewport, { className: cn('fixed z-100 flex w-full max-w-sm flex-col gap-2 p-4', VIEWPORT_POSITION_CLASS[corner]) })] }, corner));
            })] }));
}
const TONE_ICON = {
    [TOAST_TONE.SUCCESS]: CheckCircle2,
    [TOAST_TONE.ERROR]: CircleX,
    [TOAST_TONE.INFO]: Info,
    [TOAST_TONE.WARNING]: TriangleAlert,
};
const TONE_CLASS = {
    [TOAST_TONE.SUCCESS]: 'border-success/30 border-l-success bg-success/5 text-success',
    [TOAST_TONE.ERROR]: 'border-destructive/30 border-l-destructive bg-destructive/5 text-destructive',
    [TOAST_TONE.INFO]: 'border-info/30 border-l-info bg-info/5 text-info',
    [TOAST_TONE.WARNING]: 'border-warning/30 border-l-warning bg-warning/5 text-warning',
};
const TONE_ACTION_CLASS = {
    [TOAST_TONE.SUCCESS]: 'bg-success hover:bg-success/85',
    [TOAST_TONE.ERROR]: 'bg-destructive hover:bg-destructive/85',
    [TOAST_TONE.INFO]: 'bg-info hover:bg-info/85',
    [TOAST_TONE.WARNING]: 'bg-warning hover:bg-warning/85',
};
function ToastItem({ record, onDismiss, }) {
    const tone = record.tone ?? TOAST_TONE.INFO;
    const Icon = TONE_ICON[tone];
    const duration = record.persistent === true || (tone === TOAST_TONE.ERROR && record.durationMs === undefined)
        ? NEVER
        : (record.durationMs ?? DEFAULT_DURATION_MS);
    return (_jsxs(ToastPrimitive.Root, { open: true, duration: duration, onOpenChange: (open) => {
            if (!open)
                onDismiss();
        }, 
        // An error is assertive: it interrupts, because the user is about to act
        // on the belief that something worked. Anything else is polite.
        type: tone === TOAST_TONE.ERROR ? 'foreground' : 'background', className: cn('relative flex items-start gap-3 rounded-md border border-l-6 py-4 px-3 shadow-lg backdrop-blur-[10px] w-[80vw] md:max-w-[360px]', TONE_CLASS[tone]), children: [_jsx(Icon, { className: "h-6 w-6 shrink-0", "aria-hidden": "true" }), _jsxs("div", { className: "flex-1 space-y-1 pr-4", children: [_jsx(ToastPrimitive.Title, { className: "text-sm leading-5 font-semibold whitespace-nowrap", children: record.title }), record.description === undefined ? null : (_jsx(ToastPrimitive.Description, { className: "text-xs", children: record.description })), record.action === undefined ? null : (_jsx(ToastPrimitive.Action, { altText: record.action.label, onClick: record.action.onClick, className: cn('mt-1 inline-flex cursor-pointer items-center rounded-md px-3 py-2 text-xs tracking-wide font-semibold text-white transition-colors', TONE_ACTION_CLASS[tone]), children: record.action.label }))] }), _jsx(ToastPrimitive.Close, { "aria-label": "Dismiss", className: "shrink-0 opacity-70 hover:opacity-100 absolute top-2 right-2", children: _jsx(X, { className: "h-4 w-4", "aria-hidden": "true" }) })] }));
}
export function useToast() {
    const context = useContext(ToastContext);
    if (context === null) {
        throw new Error('useToast must be used inside a <ToastProvider>');
    }
    return context;
}
//# sourceMappingURL=toast.js.map